import { useState, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import CodeIcon from '@mui/icons-material/Code';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import { makeStyles } from 'tss-react/mui';
import { MarkdownContent } from '@backstage/core-components';
import type { Banner, BannerSeverity, CreateBannerInput, UpdateBannerInput } from '../../types';

const useStyles = makeStyles()((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  severityDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 8,
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  // ── Markdown editor ────────────────────────────────────────────────────────
  editorWrap: {
    marginBottom: theme.spacing(2),
  },
  editorLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  editorLabelError: {
    color: theme.palette.error.main,
  },
  editorBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`,
    },
  },
  editorBoxError: {
    borderColor: theme.palette.error.main,
    '&:focus-within': {
      borderColor: theme.palette.error.main,
      boxShadow: `0 0 0 2px ${theme.palette.error.main}22`,
    },
  },
  editorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : theme.palette.grey[50],
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  tabs: {
    minHeight: 36,
  },
  tab: {
    minHeight: 36,
    minWidth: 72,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.5, 1.5),
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  toolbarBtn: {
    padding: 4,
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary },
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box' as const,
    border: 'none',
    outline: 'none',
    resize: 'vertical' as const,
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: 13,
    lineHeight: 1.6,
    padding: theme.spacing(1.5),
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    minHeight: 110,
  },
  preview: {
    minHeight: 110,
    padding: theme.spacing(1.5),
    fontSize: 14,
    lineHeight: 1.6,
    '& p': { margin: '0 0 6px 0' },
    '& p:last-child': { margin: 0 },
    '& a': { color: theme.palette.primary.main },
    '& strong, & b': { fontWeight: '700 !important' as any },
    '& em, & i': { fontStyle: 'italic !important' as any },
    '& del, & s': { opacity: 0.6 },
    '& code': {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontSize: '0.875em',
      padding: '1px 4px',
      borderRadius: 3,
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)',
    },
  },
  previewEmpty: {
    minHeight: 110,
    padding: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: 13,
  },
  helperText: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  helperTextError: {
    color: theme.palette.error.main,
  },
}));

const SEVERITY_COLOURS: Record<BannerSeverity, string> = {
  high: '#F44336',
  medium: '#FF9800',
  low: '#4CAF50',
  info: '#03A9F4',
};

const SEVERITY_LABELS: Record<BannerSeverity, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

interface BannerFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateBannerInput | UpdateBannerInput) => Promise<void>;
  /** Existing banner to edit — omit for create mode */
  banner?: Banner;
}

export function BannerForm({ open, onClose, onSave, banner }: BannerFormProps) {
  const { classes } = useStyles();
  const isEdit = !!banner;

  const [title, setTitle] = useState(banner?.title ?? '');
  const [message, setMessage] = useState(banner?.message ?? '');
  const [severity, setSeverity] = useState<BannerSeverity>(banner?.severity ?? 'info');
  const [dismissable, setDismissable] = useState(banner?.dismissable ?? true);
  const [enableImmediately, setEnableImmediately] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string>(
    banner?.expiresAt ? banner.expiresAt.slice(0, 16) : '',
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; message?: string }>({});
  const [msgTab, setMsgTab] = useState<0 | 1>(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setTitle(banner?.title ?? '');
      setMessage(banner?.message ?? '');
      setSeverity(banner?.severity ?? 'info');
      setDismissable(banner?.dismissable ?? true);
      setEnableImmediately(false);
      setExpiresAt(banner?.expiresAt ? banner.expiresAt.slice(0, 16) : '');
      setErrors({});
      setMsgTab(0);
    }
  }, [open, banner]);

  const validate = () => {
    const e: { title?: string; message?: string } = {};
    if (!title.trim()) e.title = 'Title is required';
    if (!message.trim()) e.message = 'Message is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload: CreateBannerInput | UpdateBannerInput = {
        title: title.trim(),
        message: message.trim(),
        severity,
        dismissable,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        ...(!isEdit && { enable_immediately: enableImmediately }),
      };
      await onSave(payload);
      onClose();
    } catch {
      // error already surfaced via alertApi in the parent — keep dialog open
    } finally {
      setSaving(false);
    }
  };

  // ── Markdown toolbar helpers ───────────────────────────────────────────────
  const wrapSelection = (before: string, after: string, placeholder: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = message.slice(start, end) || placeholder;
    const next = message.slice(0, start) + before + selected + after + message.slice(end);
    setMessage(next);
    // restore selection after state update
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  const insertLink = () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = message.slice(start, end) || 'link text';
    const snippet = `[${selected}](https://example.com)`;
    const next = message.slice(0, start) + snippet + message.slice(end);
    setMessage(next);
    requestAnimationFrame(() => {
      el.focus();
      // select the URL part for easy replacement
      const urlStart = start + selected.length + 3; // after "[text]("
      el.setSelectionRange(urlStart, urlStart + 'https://example.com'.length);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Banner' : 'Create Banner'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Title"
          fullWidth
          required
          value={title}
          onChange={e => setTitle(e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          className={classes.field}
          inputProps={{ maxLength: 255 }}
        />

        {/* ── Markdown message editor ── */}
        <div className={classes.editorWrap}>
          <Typography
            component="label"
            className={`${classes.editorLabel} ${errors.message ? classes.editorLabelError : ''}`}
          >
            Message *
          </Typography>
          <div className={`${classes.editorBox} ${errors.message ? classes.editorBoxError : ''}`}>
            <div className={classes.editorHeader}>
              <Tabs
                value={msgTab}
                onChange={(_e, v) => setMsgTab(v)}
                indicatorColor="primary"
                textColor="primary"
                className={classes.tabs}
              >
                <Tab label="Write" className={classes.tab} />
                <Tab label="Preview" className={classes.tab} />
              </Tabs>
              {msgTab === 0 && (
                <div className={classes.toolbar}>
                  <Tooltip title="Bold (Ctrl+B)">
                    <IconButton
                      size="small"
                      className={classes.toolbarBtn}
                      onMouseDown={e => { e.preventDefault(); wrapSelection('**', '**', 'bold text'); }}
                    >
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Italic (Ctrl+I)">
                    <IconButton
                      size="small"
                      className={classes.toolbarBtn}
                      onMouseDown={e => { e.preventDefault(); wrapSelection('_', '_', 'italic text'); }}
                    >
                      <FormatItalicIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Strikethrough">
                    <IconButton
                      size="small"
                      className={classes.toolbarBtn}
                      onMouseDown={e => { e.preventDefault(); wrapSelection('~~', '~~', 'strikethrough'); }}
                    >
                      <FormatStrikethroughIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Inline code">
                    <IconButton
                      size="small"
                      className={classes.toolbarBtn}
                      onMouseDown={e => { e.preventDefault(); wrapSelection('`', '`', 'code'); }}
                    >
                      <CodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Insert link">
                    <IconButton
                      size="small"
                      className={classes.toolbarBtn}
                      onMouseDown={e => { e.preventDefault(); insertLink(); }}
                    >
                      <InsertLinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>
            {/* eslint-disable no-nested-ternary */}
            {msgTab === 0 ? (
              <textarea
                ref={textareaRef}
                className={classes.textarea}
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Write your banner message here. Supports **bold**, _italic_, ~~strikethrough~~, `code`, and [links](https://example.com)."
                spellCheck
              />
            ) : message.trim() ? (
              <div className={classes.preview}>
                <MarkdownContent content={message} dialect="gfm" />
              </div>
            ) : (
              <div className={classes.previewEmpty}>Nothing to preview yet.</div>
            )}
            {/* eslint-enable no-nested-ternary */}
          </div>
          <Typography
            component="p"
            className={`${classes.helperText} ${errors.message ? classes.helperTextError : ''}`}
          >
            {errors.message ?? 'Supports Markdown — bold, italic, ~~strikethrough~~, `code`, links, etc.'}
          </Typography>
        </div>

        <div className={classes.row}>
          <FormControl style={{ minWidth: 160 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={severity}
              onChange={e => setSeverity(e.target.value as BannerSeverity)}
            >
              {(Object.keys(SEVERITY_LABELS) as BannerSeverity[]).map(s => (
                <MenuItem key={s} value={s}>
                  <span
                    className={classes.severityDot}
                    style={{ backgroundColor: SEVERITY_COLOURS[s] }}
                  />
                  {SEVERITY_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={dismissable}
                onChange={e => setDismissable(e.target.checked)}
                color="primary"
              />
            }
            label="Dismissable"
          />
        </div>
        <TextField
          label="Expires at (optional)"
          type="datetime-local"
          fullWidth
          value={expiresAt}
          onChange={e => setExpiresAt(e.target.value)}
          className={classes.field}
          InputLabelProps={{ shrink: true }}
          helperText="Leave blank for no expiry. Banner will auto-expire after this date."
        />
        {!isEdit && (
          <FormControlLabel
            control={
              <Checkbox
                checked={enableImmediately}
                onChange={e => setEnableImmediately(e.target.checked)}
                color="primary"
              />
            }
            label="Enable immediately (max 3 active)"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

